import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  Checkbox,
  CheckboxGroup,
  Alert,
  AlertIcon,
  useToast,
  useColorModeValue,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Flex,
  Switch,
  Spinner,
  Divider,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@/contexts/ThemeContext';
import { useTeamService } from '@/hooks/useTeamService';

interface TeamRole {
  id: string;
  team_id: string;
  name: string;
  description?: string;
  permissions: string[];
  created_at: string;
  updated_at: string;
  is_default?: boolean;
  members_count?: number;
}

interface AvailableAction {
  action: string;
  translation: string;
  category: string;
  description?: string;
  requires_admin?: boolean;
  deprecated?: boolean;
}

interface CategoryTranslation {
  category: string;
  translation: string;
}

interface AvailableActionsResponse {
  actions: AvailableAction[];
  categories: CategoryTranslation[];
}

interface GroupedActions {
  [category: string]: AvailableAction[];
}

interface TeamRoleEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  role: TeamRole | null;
  teamId: string;
  onSuccess?: () => void;
}

export const TeamRoleEditModal: React.FC<TeamRoleEditModalProps> = ({
  isOpen,
  onClose,
  role,
  teamId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const teamService = useTeamService();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [] as string[],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [availableActions, setAvailableActions] = useState<AvailableAction[]>([]);
  const [categoryTranslations, setCategoryTranslations] = useState<{[key: string]: string}>({});
  const [groupedActions, setGroupedActions] = useState<GroupedActions>({});
  const [isLoadingActions, setIsLoadingActions] = useState(false);
  
  // Theme
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Load available actions when modal opens
  useEffect(() => {
    if (isOpen && teamId) {
      loadAvailableActions();
    }
  }, [isOpen, teamId]);

  // Initialize form when role changes
  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name || '',
        description: role.description || '',
        permissions: role.permissions || [],
      });
    } else {
      setFormData({
        name: '',
        description: '',
        permissions: [],
      });
    }
  }, [role]);

  const loadAvailableActions = async () => {
    setIsLoadingActions(true);
    try {
      const currentLanguage = t('common.languageCode', { defaultValue: 'en' });
      const response = await teamService.getTeamAvailableActions(teamId, currentLanguage);
      
      // Handle both old format (just actions array) and new format (with categories)
      let actions: AvailableAction[] = [];
      let categories: CategoryTranslation[] = [];
      
      if (Array.isArray(response)) {
        // Old format - just actions array
        actions = response;
      } else if (response && typeof response === 'object' && 'actions' in response) {
        // New format - with categories
        actions = (response as AvailableActionsResponse).actions;
        categories = (response as AvailableActionsResponse).categories || [];
      }
      
      setAvailableActions(actions);
      
      // Create category translations map
      const categoryMap: {[key: string]: string} = {};
      categories.forEach(cat => {
        categoryMap[cat.category] = cat.translation;
      });
      setCategoryTranslations(categoryMap);
      
      // Group actions by category
      const grouped = actions.reduce((acc: GroupedActions, action: AvailableAction) => {
        const category = action.category || 'General';
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(action);
        return acc;
      }, {});
      
      setGroupedActions(grouped);
    } catch (error) {
      console.error('Failed to load available actions:', error);
      toast({
        title: t('common.error'),
        description: t('teams.failedToLoadActions'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingActions(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePermissionChange = (permissions: string[]) => {
    setFormData(prev => ({
      ...prev,
      permissions,
    }));
  };

  const handleSinglePermissionToggle = (permission: string) => {
    setFormData(prev => {
      const newPermissions = prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission];
      
      return {
        ...prev,
        permissions: newPermissions,
      };
    });
  };

  const handleCategoryToggle = (category: string) => {
    const categoryActions = groupedActions[category] || [];
    const categoryPermissions = categoryActions.map(action => action.action);
    const allSelected = categoryPermissions.every(permission => 
      formData.permissions.includes(permission)
    );

    setFormData(prev => {
      let newPermissions = [...prev.permissions];
      
      if (allSelected) {
        // Remove all category permissions
        newPermissions = newPermissions.filter(p => !categoryPermissions.includes(p));
      } else {
        // Add all category permissions
        categoryPermissions.forEach(permission => {
          if (!newPermissions.includes(permission)) {
            newPermissions.push(permission);
          }
        });
      }
      
      return {
        ...prev,
        permissions: newPermissions,
      };
    });
  };

  const handleSelectAll = () => {
    const allPermissions = availableActions.map(action => action.action);
    const allSelected = allPermissions.every(permission => 
      formData.permissions.includes(permission)
    );

    setFormData(prev => ({
      ...prev,
      permissions: allSelected ? [] : allPermissions,
    }));
  };

  const isCategorySelected = (category: string) => {
    const categoryActions = groupedActions[category] || [];
    const categoryPermissions = categoryActions.map(action => action.action);
    return categoryPermissions.length > 0 && categoryPermissions.every(permission => 
      formData.permissions.includes(permission)
    );
  };

  const isCategoryPartiallySelected = (category: string) => {
    const categoryActions = groupedActions[category] || [];
    const categoryPermissions = categoryActions.map(action => action.action);
    return categoryPermissions.some(permission => 
      formData.permissions.includes(permission)
    ) && !categoryPermissions.every(permission => 
      formData.permissions.includes(permission)
    );
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast({
        title: t('common.error'),
        description: t('teams.roleNameRequired'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      console.log('Updating role:', { role, formData });
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: t('teams.roleUpdated'),
        description: t('teams.roleUpdatedSuccess'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error updating role:', error);
      toast({
        title: t('common.error'),
        description: t('teams.failedToUpdateRole'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatPermissionName = (action: AvailableAction) => {
    // Use translation from API, fallback to formatted permission name
    if (action.translation && action.translation !== action.action) {
      return action.translation;
    }
    
    // Fallback formatting
    return action.action
      .replace(/^perm_/, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatCategoryName = (category: string) => {
    // Use translation from API, fallback to formatted category name
    if (categoryTranslations[category]) {
      return categoryTranslations[category];
    }
    
    // Fallback formatting
    return category
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const isDefaultRole = role?.is_default;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent bg={bgColor} borderColor={borderColor}>
        <ModalHeader>
          {role ? t('teams.editRole') : t('teams.createRole')}
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={6} align="stretch">
            {isDefaultRole && (
              <Alert status="info">
                <AlertIcon />
                {t('teams.defaultRoleWarning')}
              </Alert>
            )}

            {/* Role Name */}
            <FormControl isRequired>
              <FormLabel>{t('teams.roleName')}</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder={t('teams.enterRoleName')}
                isDisabled={isDefaultRole}
              />
            </FormControl>

            {/* Role Description */}
            <FormControl>
              <FormLabel>{t('teams.roleDescription')}</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder={t('teams.enterRoleDescription')}
                rows={3}
              />
            </FormControl>

            {/* Permissions */}
            <FormControl>
              <FormLabel>{t('teams.permissions')}</FormLabel>
              <Text fontSize="sm" color="gray.500" mb={3}>
                {t('teams.selectPermissionsForRole')}
              </Text>
              
              {isLoadingActions ? (
                <Flex justify="center" align="center" py={8}>
                  <Spinner />
                  <Text ml={3}>{t('common.loading')}</Text>
                </Flex>
              ) : (
                <VStack spacing={4} align="stretch">
                  {/* Select All Toggle */}
                  <Flex justify="space-between" align="center" p={3} bg={useColorModeValue('gray.50', 'gray.700')} borderRadius="md">
                    <Text fontWeight="semibold">{t('teams.allPermissions')}</Text>
                    <Switch
                      isChecked={availableActions.length > 0 && availableActions.every(action => 
                        formData.permissions.includes(action.action)
                      )}
                      onChange={handleSelectAll}
                      size="lg"
                    />
                  </Flex>

                  {/* Permissions by Category */}
                  <Accordion allowMultiple defaultIndex={Object.keys(groupedActions).map((_, i) => i)}>
                    {Object.entries(groupedActions).map(([category, actions]) => (
                      <AccordionItem key={category}>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <Flex justify="space-between" align="center" w="100%">
                              <Text fontWeight="medium">{formatCategoryName(category)}</Text>
                              <HStack spacing={2}>
                                <Text fontSize="sm" color="gray.500">
                                  {formData.permissions.filter(p => actions.map(a => a.action).includes(p)).length} / {actions.length}
                                </Text>
                                <Checkbox
                                  isChecked={isCategorySelected(category)}
                                  isIndeterminate={isCategoryPartiallySelected(category)}
                                  onChange={() => handleCategoryToggle(category)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </HStack>
                            </Flex>
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel pb={4}>
                          <VStack spacing={2} align="stretch">
                            {actions.map((action) => (
                              <Flex 
                                key={action.action} 
                                justify="space-between" 
                                align="flex-start"
                                p={2}
                                borderRadius="md"
                                _hover={{ bg: useColorModeValue('gray.50', 'gray.700') }}
                              >
                                <VStack align="flex-start" spacing={0} flex={1}>
                                  <Text fontSize="sm" fontWeight="medium">
                                    {formatPermissionName(action)}
                                  </Text>
                                  {action.description && (
                                    <Text fontSize="xs" color="gray.500">
                                      {action.description}
                                    </Text>
                                  )}
                                  {action.requires_admin && (
                                    <Text fontSize="xs" color="orange.500">
                                      {t('teams.requiresAdmin')}
                                    </Text>
                                  )}
                                </VStack>
                                <Checkbox
                                  isChecked={formData.permissions.includes(action.action)}
                                  onChange={() => handleSinglePermissionToggle(action.action)}
                                  size="lg"
                                />
                              </Flex>
                            ))}
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </VStack>
              )}
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={isLoading}
              loadingText={role ? t('common.updating') : t('common.creating')}
            >
              {role ? t('common.update') : t('common.create')}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}; 